import Request from './request';
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import FieldNames from "../defaults/FieldNames.json";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import { getAppConfig, getAttributeServiceUrl, getNifcToken } from "../utils/ReduxHelper";
import { calculateFiscalYearQuarter, getSaveableDate } from "../utils/DateHelper";
import GeneralService from './GeneralService';

const InteragencyService = {
  getAllInteragencyByJoinIDFeature: async function(joinID, joinFeature) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const outFields =  `${FieldNames.interagencyFields.interagencyName}, ${FieldNames.interagencyFields.id}, ${FieldNames.interagencyFields.joinID}, ${FieldNames.interagencyFields.joinFeature}, ${FieldNames.interagencyFields.createdBy}, 
        ${FieldNames.interagencyFields.createdOnDate}, ${FieldNames.interagencyFields.lastModifiedBy}, ${FieldNames.interagencyFields.lastModifiedDate}, ${FieldNames.interagencyFields.objectID}`;
      const queryObject = new Query({
        where: `(${FieldNames.interagencyFields.joinID}=${joinID} AND ${FieldNames.interagencyFields.joinFeature}='${joinFeature}')`,
        outFields: outFields,
        returnDistinctValues: false,
        orderByFields: "OBJECTID",
        returnGeometry: false,
      });
      return query.executeQueryJSON(rootServiceUrl + "/" + config.interagency[config.env].layerId, queryObject).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features.map((eaItem) => eaItem.attributes);
        } 
        return(items)
      });    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  updateMultipleInteragency: async function (itemsForUpdating) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      let allFinalResults = [];

      const layer = `${rootServiceUrl}/${config.interagency[config.env].layerId}/updateFeatures`;

      let adjustedInteragency = [];
      itemsForUpdating.forEach((feature) => {
        const newFeature = {
          attributes: feature
        }
        adjustedInteragency.push(newFeature);
      });
      const numberToTakeEachTime = 500;
      let startCount = 0;
      let hasMoreRecords = true;
      let finalResults = "";
      while(hasMoreRecords){
        const numberToTake = ((startCount + numberToTakeEachTime) > adjustedInteragency.length) ? adjustedInteragency.length - startCount : numberToTakeEachTime;
        let numbersToRun = adjustedInteragency.slice(startCount, numberToTake + startCount);
        try{
          finalResults = await GeneralService.updateCallForMultiples(layer, nifcToken, numbersToRun);
          if(finalResults && finalResults.updateResults){
            allFinalResults.push(finalResults.updateResults)
          }
        } catch(e){
          numbersToRun.forEach(item => {
            let itemAttr = item;
            if(item.attributes){
              itemAttr = item.attributes;
            }
            const newItem = {
              objectId: itemAttr[FieldNames.interagencyFields.id],
              success: false
            }
            allFinalResults.push(newItem)
          })
        }
        hasMoreRecords = adjustedInteragency.length > (startCount + numberToTakeEachTime);
        startCount += numberToTakeEachTime;
      }
      let failedItems = [];
      let successfulItems = [];
      if(!hasMoreRecords){
        allFinalResults.flat().forEach(result => {
          if(result){
            if(!result.success){
              failedItems.push(result.objectId);
            } else {
              successfulItems.push(result.objectId);
            }
          }
        })
      }

      const allItemResults = {
        successful: successfulItems,
        failures: failedItems
      }
      return allItemResults;
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  addMultipleInteragency: async function (itemsForAdding) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      let allFinalResults = [];

      const layer = `${rootServiceUrl}/${config.interagency[config.env].layerId}/addFeatures`;

      let adjustedInteragency = [];
      itemsForAdding.forEach((feature) => {
        const newFeature = {
          attributes: feature
        }
        adjustedInteragency.push(newFeature);
      });
      const numberToTakeEachTime = 500;
      let startCount = 0;
      let hasMoreRecords = true;
      let finalResults = "";
      while(hasMoreRecords){
        const numberToTake = ((startCount + numberToTakeEachTime) > adjustedInteragency.length) ? adjustedInteragency.length - startCount : numberToTakeEachTime;
        let numbersToRun = adjustedInteragency.slice(startCount, numberToTake + startCount);
        try{
          finalResults = await GeneralService.updateCallForMultiples(layer, nifcToken, numbersToRun);
          if(finalResults && finalResults.addResults){
            allFinalResults.push(finalResults.addResults)
          }
        } catch(e){
          numbersToRun.forEach(item => {
            let itemAttr = item;
            if(item.attributes){
              itemAttr = item.attributes;
            }
            const newItem = {
              objectId: itemAttr[FieldNames.interagencyFields.id],
              success: false
            }
            allFinalResults.push(newItem)
          })
        }
        hasMoreRecords = adjustedInteragency.length > (startCount + numberToTakeEachTime);
        startCount += numberToTakeEachTime;
      }
      let failedItems = [];
      let successfulItems = [];
      if(!hasMoreRecords){
        allFinalResults.flat().forEach(result => {
          if(result){
            if(!result.success){
              failedItems.push(result.objectId);
            } else {
              successfulItems.push(result.objectId);
            }
          }
        })
      }
      const allItemResults = {
        successful: successfulItems,
        failures: failedItems
      }
      return allItemResults;
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  deleteMultipleInteragency: function (interagency) {
    try
    {
      //Using this to delete roles before adding new ones
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      return new Promise((resolve, reject) => {
        let objectIds = [];
        interagency.forEach((ia) => {
            objectIds.push(ia.OBJECTID);
        })
        const objectIdsForRemoval = objectIds.join(", ");
          new Request().makeRequest({
              url: rootServiceUrl + "/" + config.interagency[config.env].layerId + "/deleteFeatures",
              method: 'post',
              data: {
                  f: 'json',
                  token: nifcToken,
                  objectIds: objectIdsForRemoval
              }
          }).then(
              resp => {
                  resolve(resp)
              },
              error => {
                reject(error)
              }
          );
          
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
};

export default InteragencyService;